import { useEffect, useState } from "react";
import { packagesApi } from "@/services/api";
import { useLanguage } from "@/components/providers/language-provider";
import { formatPrice } from "@/utils/formatPrice";

interface LocalizedPrice {
  price: number;
  currency: string;
  symbol: string;
}

interface WebPackage {
  price: number;
  schedule: string;
  localizedPrices?: Record<string, LocalizedPrice> | null;
}

// Marketing fallbacks when the API is unreachable (base/USD).
const FALLBACK: Record<string, number> = { year: 6, lifetime: 12 };

/**
 * Live package prices for marketing pages, localized per app language via
 * Package.localizedPrices ({ pt: { price, currency, symbol }, ... }); the
 * base `price` (USD $) is the fallback.
 */
export function usePackagePrices() {
  const { language } = useLanguage();
  const [packages, setPackages] = useState<WebPackage[]>([]);

  useEffect(() => {
    let cancelled = false;
    packagesApi
      .getWebPackages()
      .then((data) => {
        if (!cancelled && Array.isArray(data)) setPackages(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const display = (schedule: string): string => {
    const pkg = packages.find((p) => p.schedule === schedule);
    const lp = pkg?.localizedPrices?.[language];
    const amount = lp ? lp.price : pkg?.price ?? FALLBACK[schedule] ?? 0;
    const currency = lp ? lp.currency : "USD";
    return formatPrice(amount, currency, language, lp?.symbol ?? "$");
  };

  return { display };
}
